/*  GuestEntry.jsx  */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, LogOut, ArrowLeftCircle } from 'lucide-react';
import API   from '../services/api';
import '../styles/Dashboard.css';

const PER_PAGE = 50;

export default function GuestEntry() {
  /* ───────── state ───────── */
  const [list, setList]       = useState([]);       // all guests
  const [tab,  setTab]        = useState('in');     // 'in' | 'out'
  const [search, setSearch]   = useState('');
  const [page,   setPage]     = useState(1);
  const [show,   setShow]     = useState(false);    // show “new guest” modal
  const [form,   setForm]     = useState({
    name:'', cnic:'', purpose:'', visitingOffice:''
  });

  const nav = useNavigate();
  const resetForm = () =>
    setForm({ name:'', cnic:'', purpose:'', visitingOffice:'' });

  /* ───────── refresh helper ───────── */
  const refresh = useCallback(async () => {
    try {
      const { data } = await API.get('/guests');
      setList(data);
    } catch (e) {
      console.error('Load guests error', e);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  /* ───────── insight chips ───────── */
  const insideCnt = list.filter(g => g.status === 'in').length;
  const outCnt    = list.filter(g => g.status === 'out').length;
  const totalCnt  = list.length;

  /* ───────── add guest ───────── */
  const saveGuest = async e => {
    e.preventDefault();
    try {
      await API.post('/guests', form);          // backend must default status = 'in'
      resetForm();
      setShow(false);
      refresh();
    } catch (err) {
      console.error(err);
    }
  };

  /* ───────── mark IN / OUT ───────── */
  const markStatus = async (id, next) => {
    try {
      await API.put(`/guests/${id}`, { status: next });
      refresh();
    } catch (err) {
      console.error(err);
    }
  };

  /* ───────── search & pagination ───────── */
  const filtered = useMemo(() => {
    const base = list.filter(g => g.status === tab);
    if (!search.trim()) return base;
    const q = search.toLowerCase();
    return base.filter(g =>
      [g.name, g.cnic, g.purpose, g.visitingOffice]
        .some(f => f?.toLowerCase().includes(q))
    );
  }, [list, tab, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const rows       = filtered.slice((page-1)*PER_PAGE, page*PER_PAGE);

  /* ───────── inline btn style generators ───────── */
  const btn = (bg) => ({
    display:'flex',alignItems:'center',gap:4,
    padding:'5px 14px',border:'none',borderRadius:6,
    fontSize:'0.8rem',cursor:'pointer',color:'#fff',
    background:bg
  });

  /* ───────── UI ───────── */
  return (
    <div className="dashboard-container">
      {/* header */}
      <div className="dashboard-header">
        <h2>📝 Guest Register</h2>

        {/* insight chips */}
        <div className="counters" style={{marginTop:0}}>
          <div className="counter-item" style={{background:'#d1fae5'}}>
            <div>Inside</div><div>{insideCnt}</div>
          </div>
          <div className="counter-item" style={{background:'#fee2e2'}}>
            <div>Left</div><div>{outCnt}</div>
          </div>
          <div className="counter-item" style={{background:'#f3f4f6'}}>
            <div>Total</div><div>{totalCnt}</div>
          </div>
        </div>

        {/* nav back to leave-requests */}
        <button
          style={btn('#64748b')}
          onClick={() => nav('/gatekeeper')}
          title="Back to Leave-Requests"
        >
          <ArrowLeftCircle size={16}/> Back
        </button>
      </div>

      {/* tab switch */}
      <div style={{marginBottom:12}}>
        {['in','out'].map(t=>(
          <button key={t}
            className={`period-btn ${tab===t?'active':''}`}
            onClick={()=>{setTab(t);setPage(1);}}>
            {t==='in'?'Current Guests':'Previous Visitors'}
          </button>
        ))}
        <button
          className="action-btn"
          style={{float:'right'}}
          onClick={()=>setShow(true)}>
          New&nbsp;Guest
        </button>
      </div>

      {/* search */}
      <div className="search-filter">
        <input
          placeholder="Search guests…"
          value={search}
          onChange={e=>{setSearch(e.target.value);setPage(1);}}
        />
      </div>

      {/* table */}
      <div className="table-container prettified">
        <table>
          <thead>
            <tr>
              <th style={{width:150}}>Date &amp; Time</th>
              <th>Name</th><th>CNIC</th><th>Purpose</th><th>Visiting Office</th>
              {tab==='in' && <th style={{width:90}}>Action</th>}
            </tr>
          </thead>
          <tbody>
          {rows.length ? rows.map(g=>(
            <tr key={g._id}>
              <td>{new Date(g.createdAt).toLocaleString()}</td>
              <td>{g.name}</td>
              <td>{g.cnic}</td>
              <td>{g.purpose}</td>
              <td>{g.visitingOffice}</td>

              {/* IN first, then OUT ─ only show opposite of current status */}
              {tab==='in' && (
                <td>
                  <button
                    style={btn('#f97316')}
                    title="Mark guest OUT"
                    onClick={()=>markStatus(g._id,'out')}>
                    <LogOut size={16}/> OUT
                  </button>
                </td>
              )}
            </tr>
          )) : (
            <tr><td colSpan={tab==='in'?6:5} style={{textAlign:'center',padding:16}}>
              No records found
            </td></tr>
          )}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      <div className="pagination">
        <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}>Prev</button>
        <span className="page-info">Page {page} of {totalPages}</span>
        <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}>Next</button>
      </div>

      {/* New-guest modal */}
      {show && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header"><h3>New Guest</h3></div>
            <form className="modal-body" onSubmit={saveGuest}>
              {[
                ['name','Name of Guest'],
                ['cnic','CNIC (13 digits)'],
                ['purpose','Purpose of Visit'],
                ['visitingOffice','Visiting Office']
              ].map(([k,label])=>(
                <div className="form-group" key={k}>
                  <label>{label}</label>
                  <input
                    value={form[k]}
                    onChange={e=>setForm({...form,[k]:e.target.value})}
                    required
                  />
                </div>
              ))}
              <div className="modal-footer">
                <button type="submit">Save</button>
                <button type="button" className="cancel-btn-form"
                        onClick={()=>{setShow(false);resetForm();}}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
